import { useEffect, useState, useRef } from 'react';

interface ElevenLabsWidgetProps {
  agentId: string;
}

const ElevenLabsWidget: React.FC<ElevenLabsWidgetProps> = ({ agentId }) => {
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const cleanupRef = useRef<(() => void) | null>(null);
  const widgetCreatedRef = useRef(false);

  useEffect(() => {
    // Only create the widget element once
    if (!widgetCreatedRef.current && containerRef.current) {
      const widgetElement = document.createElement('elevenlabs-convai');
      widgetElement.setAttribute('agent-id', agentId);
      containerRef.current.appendChild(widgetElement);
      widgetCreatedRef.current = true;
    }

    // Aggressive watermark prevention CSS - inject immediately
    const aggressiveStyle = document.createElement('style');
    aggressiveStyle.textContent = `
      /* Global watermark prevention */
      * {
        --watermark-display: none !important;
      }
      
      /* Hide all potential watermark elements immediately */
      [class*="watermark"], [class*="branding"], [class*="powered"], 
      [class*="logo"], [class*="footer"], [class*="attribution"], 
      [class*="credit"], [class*="badge"], [class*="label"],
      [id*="watermark"], [id*="branding"], [id*="powered"],
      [data-testid*="watermark"], [data-testid*="branding"] {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
        position: absolute !important;
        left: -9999px !important;
        top: -9999px !important;
        width: 0 !important;
        height: 0 !important;
        overflow: hidden !important;
      }
      
      /* Hide all links to ElevenLabs */
      a[href*="elevenlabs"], a[href*="11labs"], a[href*="elevenlabs.io"] {
        display: none !important;
        visibility: hidden !important;
      }
      
      /* Hide small text elements that could be watermarks */
      [style*="font-size: 8px"], [style*="font-size: 9px"],
      [style*="font-size: 10px"], [style*="font-size: 11px"], 
      [style*="font-size: 12px"] {
        display: none !important;
      }
      
      /* Hide positioned elements at bottom */
      [style*="position: absolute"][style*="bottom"], 
      [style*="position: fixed"][style*="bottom"],
      [style*="position: absolute"][style*="right"],
      [style*="position: fixed"][style*="right"] {
        display: none !important;
      }
      
      /* Hide elements with low opacity */
      [style*="opacity: 0.1"], [style*="opacity: 0.2"], 
      [style*="opacity: 0.3"], [style*="opacity: 0.4"],
      [style*="opacity: 0.5"], [style*="opacity: 0.6"],
      [style*="opacity: 0.7"], [style*="opacity: 0.8"] {
        display: none !important;
      }
      
      /* Hide gray/muted text */
      [style*="color: gray"], [style*="color: grey"], 
      [style*="color: #999"], [style*="color: #666"],
      [style*="color: #888"], [style*="color: #777"],
      [style*="color: rgba(0,0,0,0.5)"], [style*="color: rgba(0,0,0,0.6)"],
      [style*="color: rgba(0,0,0,0.7)"] {
        display: none !important;
      }
      
      /* ElevenLabs specific targeting */
      elevenlabs-convai::part(watermark),
      elevenlabs-convai::part(branding),
      elevenlabs-convai::part(footer),
      elevenlabs-convai::part(attribution) {
        display: none !important;
      }
      
      /* Hide the widget container initially to prevent flash */
      .elevenlabs-widget-container {
        opacity: 0 !important;
        transition: opacity 0.3s ease-in-out;
      }
      
      .elevenlabs-widget-container.loaded {
        opacity: 1 !important;
      }
    `;
    
    document.head.appendChild(aggressiveStyle);

    // Aggressive element removal function
    const removeAllWatermarks = () => {
      // Remove from regular DOM
      const allPotentialWatermarks = document.querySelectorAll([
        '[class*="watermark"]', '[class*="branding"]', '[class*="powered"]',
        '[class*="logo"]', '[class*="footer"]', '[class*="attribution"]',
        '[class*="credit"]', '[class*="badge"]', '[class*="label"]',
        '[id*="watermark"]', '[id*="branding"]', '[id*="powered"]',
        'a[href*="elevenlabs"]', 'a[href*="11labs"]'
      ].join(', '));

      allPotentialWatermarks.forEach(el => {
        const text = el.textContent?.toLowerCase() || '';
        if (text.includes('powered by') || text.includes('elevenlabs') || 
            text.includes('11labs') || text.includes('ai by') ||
            el.getAttribute('href')?.includes('elevenlabs')) {
          el.remove();
        }
      });

      // Check shadow DOM
      const widget = document.querySelector('elevenlabs-convai');
      if (widget && (widget as any).shadowRoot) {
        const shadowRoot = (widget as any).shadowRoot;
        
        // Remove watermark elements in shadow DOM
        const shadowWatermarks = shadowRoot.querySelectorAll([
          '[class*="watermark"]', '[class*="branding"]', '[class*="powered"]',
          '[class*="logo"]', '[class*="footer"]', '[class*="attribution"]',
          '[class*="credit"]', 'a[href*="elevenlabs"]'
        ].join(', '));
        
        shadowWatermarks.forEach((el: Element) => el.remove());

        // Remove text nodes with watermark content
        const walker = document.createTreeWalker(
          shadowRoot,
          NodeFilter.SHOW_TEXT
        );

        const textNodesToRemove: Node[] = [];
        let node;
        while (node = walker.nextNode()) {
          const text = node.textContent?.toLowerCase() || '';
          if (text.includes('powered by') || text.includes('elevenlabs') || 
              text.includes('11labs') || text.includes('ai by')) {
            textNodesToRemove.push(node);
          }
        }

        textNodesToRemove.forEach(textNode => {
          textNode.parentNode?.removeChild(textNode);
        });
      }
    };

    // Start aggressive removal immediately
    const immediateInterval = setInterval(removeAllWatermarks, 50);
    
    // Set up mutation observer for real-time removal
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length > 0) {
          removeAllWatermarks();
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeOldValue: true
    });

    // Check for script loading
    const checkScriptLoaded = () => {
      if (window.customElements && window.customElements.get('elevenlabs-convai')) {
        setScriptLoaded(true);
        // Add loaded class after a delay to ensure watermarks are removed
        setTimeout(() => {
          if (containerRef.current) {
            containerRef.current.classList.add('loaded');
          }
        }, 1000);
        return;
      }
      setTimeout(checkScriptLoaded, 100);
    };

    checkScriptLoaded();

    // Cleanup function
    cleanupRef.current = () => {
      clearInterval(immediateInterval);
      observer.disconnect();
      if (aggressiveStyle.parentNode) {
        aggressiveStyle.parentNode.removeChild(aggressiveStyle);
      }
    };

    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
      }
    };
  }, [agentId]);

  return (
    <div 
      ref={containerRef}
      className="elevenlabs-widget-container"
    />
  );
};

export default ElevenLabsWidget;